import {ConfigProvider, Image} from 'antd';
import {ReactElement, useMemo} from 'react';

import {classNames} from '../../../../../../../util/css';
import {usePreviewVideoController} from '../../../../review-preview-video';

import {buildGalleryMediaItems, isGalleryVideoMediaItem} from './preview-group-media';
import {PreviewGroupVideoTile} from './preview-group-video-tile';
import * as styles from './preview-group.scss';

type ImageItemType = {src: string | null; alt: string | null} | HTMLImageElement;

type PropsType = {
    images?: Array<ImageItemType>;
    videos?: Array<{src: string | null; poster: string | null}>;
    imageClassName?: string;
};

export function PreviewGroup(props: PropsType): ReactElement | null {
    const {images, videos, imageClassName} = props;
    const {previewClassName, renderEmptyPreviewToolbar, renderVideo, stop} = usePreviewVideoController();

    const mediaItems = useMemo(() => buildGalleryMediaItems(images, videos), [images, videos]);

    const groupPreview = useMemo(
        () => ({
            className: previewClassName,
            imageRender: (originalNode: ReactElement, info: {current: number}) => {
                const item = mediaItems[info.current];

                if (isGalleryVideoMediaItem(item)) {
                    return renderVideo(item.src);
                }

                return originalNode;
            },
            toolbarRender: (originalNode: ReactElement, info: {current: number}) => {
                if (isGalleryVideoMediaItem(mediaItems[info.current])) {
                    return renderEmptyPreviewToolbar();
                }

                return originalNode;
            },
            onVisibleChange: (visible: boolean) => {
                if (!visible) {
                    stop();
                }
            },
            onChange: () => {
                stop();
            },
        }),
        [mediaItems, previewClassName, renderEmptyPreviewToolbar, renderVideo, stop]
    );

    if (mediaItems.length === 0) {
        return null;
    }

    return (
        <ConfigProvider
            locale={{
                locale: '',
                Image: {preview: ''},
            }}
        >
            <div className={classNames(styles.ImageGallery, styles.ImageGallery_list)}>
                <div className={styles.ImageGallery_track}>
                    <Image.PreviewGroup preview={groupPreview}>
                        {mediaItems.map((item) => {
                            if (item.type === 'image') {
                                return (
                                    <Image
                                        // eslint-disable-next-line no-undefined
                                        alt={item.alt ?? undefined}
                                        className={classNames(styles.ImageGallery_image, imageClassName)}
                                        key={item.key}
                                        preview={{maskClassName: styles.ImageGallery_mask}}
                                        src={item.src}
                                        wrapperClassName={styles.ImageGallery_wrapper}
                                    />
                                );
                            }

                            return (
                                <PreviewGroupVideoTile
                                    imageClassName={imageClassName}
                                    key={item.key}
                                    poster={item.poster}
                                    previewSrc={item.previewSrc}
                                    videoSrc={item.src}
                                />
                            );
                        })}
                    </Image.PreviewGroup>
                </div>
            </div>
        </ConfigProvider>
    );
}
